/**
 * Foundation SDK — typed API client for the Foundation platform.
 *
 * One input: a config URL. The SDK fetches it, discovers auth, API URLs,
 * features, plans, theme, connectors — everything. Self-configuring.
 */
import type {
  Foundation,
  FoundationConfig,
  FullConfig,
  AuthService,
  DbService,
  FilesService,
  AccountService,
  BillingDefaults,
  BillingService,
  BillingPlan,
  CheckoutSessionStatus,
  AccountSubscription,
  ConfigPlan,
  IntegrationService,
  Integration,
  IntegrationConnection,
  IntegrationDetail,
  OAuthService,
  OpenApiService,
  LogService,
} from './types'
import { createHttpClient, unwrapResponse, type HttpClient } from './http'
import { createAuthClient, createAuthService, type AuthProviderConfig } from './auth'
import { createRealtimeClient } from './realtime'
import { parseJwt } from './jwt'

export async function createFoundation(options: FoundationConfig): Promise<Foundation> {
  // 1. Resolve config URL — foundation-env.json wins if it exists (deployed env)
  let configUrl = options.configUrl
  let resolvedAppId = options.appId
  let resolvedTenantId = options.tenantId

  try {
    const envResponse = await fetch('/foundation-env.json')
    if (envResponse.ok) {
      const env = await envResponse.json()
      if (env.configUrl) {
        configUrl = env.configUrl
        resolvedAppId = env.applicationId || resolvedAppId
        resolvedTenantId = env.applicationTenant || resolvedTenantId
        // Real env — clear dev-only baseUrl override
        options.baseUrl = undefined
      }
    }
  } catch { /* no env file — use passed options */ }

  if (!configUrl) {
    throw new Error('No configUrl provided and /foundation-env.json not found')
  }

  // 2. Fetch public config
  const configHeaders: Record<string, string> = { 'Accept': 'application/json', 'Cache-Control': 'no-cache' }
  if (resolvedAppId) configHeaders['X-Foundation-Mvp-Application-Id'] = resolvedAppId
  if (resolvedTenantId) configHeaders['X-Foundation-Mvp-Tenant-Id'] = resolvedTenantId

  const publicConfigResponse = await fetch(configUrl, { headers: configHeaders })
  if (!publicConfigResponse.ok) {
    throw new Error(`Failed to fetch config: ${publicConfigResponse.statusText}`)
  }
  const rawPublicConfig = await publicConfigResponse.json()
  const publicConfig = rawPublicConfig.data ?? rawPublicConfig

  // 2. Determine API URLs (pre-auth fallback from public config; JWT-based URLs resolved lazily after auth)
  const authConfig: AuthProviderConfig = publicConfig.auth || { provider: 'none' }
  const appId = resolvedAppId || publicConfig.app?.id || publicConfig.tenant?.identifier || ''
  const tenantId = resolvedTenantId || publicConfig.tenant?.identifier || ''
  const version = publicConfig.app?.version || publicConfig.core?.version || '0.0.0'

  let apiBaseUrl = ''
  let accountBaseUrl = ''

  if (options.baseUrl) {
    apiBaseUrl = options.baseUrl
    accountBaseUrl = options.baseUrl
  } else if ((authConfig as Record<string, any>).apiUrls?.apiBaseUrl) {
    const apiUrls = (authConfig as Record<string, any>).apiUrls
    apiBaseUrl = apiUrls.apiBaseUrl
    accountBaseUrl = apiUrls.accountBaseUrl || apiBaseUrl
  }
  // else: will be resolved from JWT after auth

  // 3. Set up auth with context for backend endpoint calls
  const authCtx = { apiBaseUrl, accountBaseUrl, appId, tenantId, version }
  let authClient: import('./types').AuthClient

  if (options.auth && typeof options.auth === 'function') {
    authClient = await options.auth(authConfig, authCtx)
  } else if (options.auth) {
    authClient = options.auth as import('./types').AuthClient
  } else {
    authClient = await createAuthClient(authConfig, authCtx)
  }
  const authServiceRaw = createAuthService(authClient, { autoSignInAfterConfirm: options.autoSignInAfterConfirm })
  const authService = authServiceRaw as AuthService & { _initUser(): Promise<void> }
  await authService._initUser()

  // 4. Resolve API URLs from JWT if not already known
  if (!apiBaseUrl) {
    const token = await authClient.getTokenSilently()
    const claims = parseJwt(token)
    apiBaseUrl = claims.apiBaseUrl
    accountBaseUrl = claims.accountBaseUrl
    // Update context for any later use (though providers have already been constructed)
    authCtx.apiBaseUrl = apiBaseUrl
    authCtx.accountBaseUrl = accountBaseUrl
  }

  const http = createHttpClient({
    appId,
    tenantId,
    version,
    getToken: () => authClient.getTokenSilently()
  })

  // 5. Fetch backend config (authenticated)
  let backendConfig: Record<string, unknown> = {}
  try {
    const cfg = await http.request(apiBaseUrl, '/api/v1/config/init', 'GET')
    backendConfig = (cfg || {}) as Record<string, unknown>
  } catch (err) {
    console.warn('[Foundation SDK] Backend config fetch failed:', err)
  }

  // 6. Merge configs
  const mergedConfig = { ...publicConfig, ...backendConfig }

  // Determine namespace from JWT if available
  let namespace = ''
  try {
    const token = await authClient.getTokenSilently()
    if (token && token !== 'none') {
      const claims = parseJwt(token)
      namespace = claims.namespace || ''
    }
  } catch { /* no token yet */ }

  // 7. Build config service
  const config: FullConfig = {
    get app() {
      const app = mergedConfig.app as Record<string, unknown> || {}
      return {
        id: (app.id || appId) as string,
        name: (app.name || '') as string,
        version: (app.version || version) as string,
        environment: (app.environment || '') as string,
        ...app
      }
    },
    get features() { return (mergedConfig.features || {}) as Record<string, unknown> },
    get plans() { return (mergedConfig.plans || []) as ConfigPlan[] },
    get theme() {
      const theme = mergedConfig.theme as Record<string, unknown> || {}
      return {
        colors: (theme.colors || {}) as Record<string, string>,
        dark: (theme.dark || {}) as Record<string, string>,
        defaultColorScheme: theme.defaultColorScheme as string | undefined
      }
    },
    get connectors() { return (mergedConfig.connectors || {}) as Record<string, unknown> },
    get resources() { return (mergedConfig.resources || {}) as Record<string, string> },
    get auth() {
      const { provider, ...rest } = authConfig
      return { provider, ...rest } as { provider: string; [key: string]: unknown }
    },
    get raw() { return mergedConfig }
  }

  // 8. Build services
  const db = createDbService(http, apiBaseUrl)
  const files = createFilesService(http, apiBaseUrl, namespace)
  const account = createAccountService(http, accountBaseUrl)
  const billing = createBillingService(http, accountBaseUrl, options.billing)
  const integration = createIntegrationService(http, apiBaseUrl, accountBaseUrl, namespace)
  const oauth = createOAuthService(http, apiBaseUrl)
  const openapi = createOpenApiService(http, apiBaseUrl)
  const log = createLogService()

  // Realtime entity-change events over WebSocket (lazy: connects on first subscriber)
  const realtime = createRealtimeClient({
    getToken: () => authClient.getTokenSilently(),
    getWebsocketBaseUrl: (token) => {
      try {
        return parseJwt(token).websocketBaseUrl
      } catch {
        return ''
      }
    },
    warn: (message, context) => console.warn(`[Foundation SDK] ${message}`, context ?? '')
  })

  const foundation: Foundation = {
    get ready() { return Promise.resolve() },
    get isReady() { return true },
    auth: authService,
    db,
    files,
    integration,
    account,
    billing,
    config,
    oauth,
    openapi,
    log,
    on(event, callback) {
      if (event === 'entity.changed') {
        return realtime.subscribe(callback)
      }
      return () => {}
    }
  }

  return foundation
}

// --- Service factories ---

export function createDbService(http: HttpClient, apiBase: string): DbService {
  return {
    async list(entity, options = {}) {
      const { template, filters, limit, cursor, orderBy, orderDir } = options
      const params: Record<string, unknown> = { ...filters }
      // Explicit template option wins over a `query` key smuggled into filters
      if (template) params.query = template
      if (limit) params.limit = limit
      if (cursor) params.next = cursor
      if (orderBy) params.orderBy = orderBy
      if (orderDir) params.orderDir = orderDir
      return http.request(apiBase, `/api/v1/core/${entity}`, 'GET', { params }) as Promise<any>
    },
    async get(entity, id) {
      return http.request(apiBase, `/api/v1/core/${entity}`, 'GET', { params: { id } }) as Promise<any>
    },
    async create(entity, data) {
      return http.request(apiBase, `/api/v1/core/${entity}`, 'POST', { body: data }) as Promise<any>
    },
    async update(entity, id, updates) {
      return http.request(apiBase, `/api/v1/core/${entity}`, 'PUT', { body: { id, ...updates } }) as Promise<any>
    },
    async save(entity, data) {
      try {
        return await http.request(apiBase, `/api/v1/core/${entity}`, 'PUT', { body: data }) as Promise<any>
      } catch {
        return http.request(apiBase, `/api/v1/core/${entity}`, 'POST', { body: data }) as Promise<any>
      }
    },
    async delete(entity, id) {
      await http.request(apiBase, `/api/v1/core/${entity}`, 'DELETE', { body: { id } })
    }
  }
}

async function computeSha256Base64(data: ArrayBuffer): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error(
      'Cannot compute the file\'s SHA-256 hash: crypto.subtle is unavailable. ' +
      'Browsers only expose it in secure contexts (HTTPS or localhost). ' +
      'Serve the page over HTTPS, or pass a base64-encoded sha256 to upload() yourself.'
    )
  }
  const digest = await crypto.subtle.digest('SHA-256', data)
  let binary = ''
  for (const byte of new Uint8Array(digest)) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

export function createFilesService(http: HttpClient, apiBase: string, namespace: string): FilesService {
  return {
    async initiate(options) {
      return http.request(apiBase, '/api/v1/core/upload', 'POST', {
        body: { ...options, __namespace: namespace }
      }) as Promise<any>
    },
    async upload(options) {
      let fileData: ArrayBuffer
      if (options.file instanceof ArrayBuffer) {
        fileData = options.file
      } else {
        fileData = await options.file.arrayBuffer()
      }

      const sha256 = options.sha256 ?? await computeSha256Base64(fileData)

      const uploadData = await http.request(apiBase, '/api/v1/core/upload', 'POST', {
        body: { name: options.name, contentType: options.contentType, contentLength: fileData.byteLength, sha256, __namespace: namespace }
      }) as { id: string; name: string; signedUrl: string; signedData: Record<string, string> }

      if (!uploadData.signedUrl || !uploadData.signedData) {
        throw new Error('Missing signedUrl or signedData in response')
      }

      const formData = new FormData()
      Object.entries(uploadData.signedData).forEach(([key, value]) => {
        formData.append(key, value)
      })
      formData.append('file', new Blob([fileData], { type: options.contentType }), options.name)

      const s3Response = await fetch(uploadData.signedUrl, { method: 'POST', body: formData })
      if (s3Response.status !== 204) {
        throw new Error(`S3 upload failed: ${s3Response.status}`)
      }

      return { id: uploadData.id, name: uploadData.name, status: 'uploaded', s3UploadComplete: true }
    },
    async get(fileId) {
      return http.request(apiBase, '/api/v1/core/files', 'GET', {
        params: { id: fileId, __namespace: namespace }
      }) as Promise<any>
    },
    async delete(fileId) {
      await http.request(apiBase, `/api/v1/core/files/${fileId}`, 'DELETE')
    },
    async list(options = {}) {
      const params: Record<string, unknown> = { __namespace: namespace }
      if (options.limit) params.limit = options.limit
      if (options.cursor) params.cursor = options.cursor
      const result = await http.request(apiBase, '/api/v1/core/files', 'GET', { params }) as {
        items?: unknown[]; next?: string
      }
      return { items: result?.items || [], nextCursor: result?.next } as any
    }
  }
}

export function createBillingService(
  http: HttpClient,
  accountBase: string,
  defaults: BillingDefaults = {}
): BillingService {
  return {
    async plans() {
      const result = await http.request(accountBase, '/api/v1/accounts/billing/plans', 'GET') as { plans?: BillingPlan[] }
      return result?.plans ?? []
    },
    async checkout(planId, options = {}) {
      if (!planId) {
        throw new Error("billing.checkout requires a planId (Stripe price id)")
      }
      const body: Record<string, unknown> = { planId }
      // Resolution: per-call > SDK config > omit (backend convention applies).
      const successUrl = options.successUrl ?? defaults.successUrl
      const cancelUrl = options.cancelUrl ?? defaults.cancelUrl
      if (successUrl) body.successUrl = successUrl
      if (cancelUrl) body.cancelUrl = cancelUrl
      return http.request(accountBase, '/api/v1/accounts/billing/checkout', 'POST', { body }) as Promise<{ url: string }>
    },
    async portal(options = {}) {
      if (options.flow === 'subscription_update_confirm' && !options.targetPriceId) {
        throw new Error("billing.portal flow 'subscription_update_confirm' requires targetPriceId")
      }
      const body: Record<string, unknown> = {}
      const returnUrl = options.returnUrl ?? defaults.returnUrl
      if (returnUrl) body.returnUrl = returnUrl
      if (options.flow) body.flow = options.flow
      if (options.targetPriceId) body.targetPriceId = options.targetPriceId
      return http.request(accountBase, '/api/v1/accounts/billing/customer-portal', 'POST', { body }) as Promise<{ url: string }>
    },
    async lookupSession(sessionId) {
      if (!sessionId) {
        throw new Error("billing.lookupSession requires a sessionId")
      }
      return http.request(accountBase, '/api/v1/accounts/billing/lookup-session', 'GET', {
        params: { sessionId }
      }) as Promise<CheckoutSessionStatus>
    },
    async subscriptions() {
      const result = await http.request(accountBase, '/api/v1/accounts/billing/subscriptions', 'GET') as { subscriptions?: AccountSubscription[] }
      return result?.subscriptions ?? []
    }
  }
}

function createAccountService(http: HttpClient, accountBase: string): AccountService {
  return {
    async get() {
      return http.request(accountBase, '/api/v1/accounts/account', 'GET') as Promise<any>
    },
    async update(data) {
      await http.request(accountBase, '/api/v1/accounts/account', 'PUT', { body: { user: data } })
    },
    async usage() {
      return http.request(accountBase, '/api/v1/accounts/account/usage', 'GET') as Promise<any>
    },
    async resendVerification() {
      await http.request(accountBase, '/api/v1/accounts/account/resend-verification', 'POST')
    }
  }
}

function createIntegrationService(http: HttpClient, apiBase: string, accountBase: string, namespace: string): IntegrationService {
  const service: IntegrationService = {
    async list() {
      return http.request(apiBase, '/api/v1/config/connectors', 'GET') as Promise<any>
    },
    async connections() {
      return http.request(apiBase, '/api/v1/core/integrations', 'GET', {
        params: { query: 'default', __namespace: namespace }
      }) as Promise<any>
    },
    async all() {
      const [rawCatalog, rawConnections] = await Promise.all([
        service.list(),
        service.connections()
      ])

      const catalog = (Array.isArray(rawCatalog) ? rawCatalog : (rawCatalog as any)?.items || []) as Integration[]
      const connections = (Array.isArray(rawConnections) ? rawConnections : (rawConnections as any)?.items || []) as IntegrationConnection[]

      const bySource = new Map<string, IntegrationConnection[]>()
      for (const conn of connections) {
        const source = conn.source || conn.id
        if (!bySource.has(source)) bySource.set(source, [])
        bySource.get(source)!.push(conn)
      }

      return catalog.map(item => {
        const conns = bySource.get(item.id) || []
        return {
          ...item,
          connections: conns,
          connected: conns.some(c => c.connected),
          connectionCount: conns.filter(c => c.connected).length
        } as IntegrationDetail
      })
    },
    async status(source) {
      const conns = await service.connections()
      const items = (Array.isArray(conns) ? conns : (conns as any)?.items || []) as IntegrationConnection[]
      const filtered = items.filter(c => c.source === source && c.connected)
      return { connected: filtered.length > 0, connections: filtered }
    },
    async connect(source) {
      return http.request(accountBase, `/api/v1/accounts/integrations/${source}/initialize`, 'POST', {
        body: { __namespace: namespace }
      }) as Promise<any>
    },
    async disconnect(source, configurationId) {
      await http.request(accountBase, `/api/v1/accounts/integrations/${source}/remove`, 'POST', {
        body: { configurationId }
      })
    }
  }
  return service
}

function createOAuthService(http: HttpClient, apiBase: string): OAuthService {
  return {
    async getClient(clientId) {
      return http.request(apiBase, '/api/v1/core/oauth-clients', 'GET', {
        params: { id: clientId }
      }) as Promise<any>
    },
    async authorizeConsent(params) {
      return http.request(apiBase, '/api/v1/oauth/authorize-consent', 'POST', {
        body: params
      }) as Promise<any>
    }
  }
}

function createOpenApiService(http: HttpClient, apiBase: string): OpenApiService {
  return {
    async get() {
      return http.request(apiBase, '/api/v1/config/openapi', 'GET') as Promise<any>
    }
  }
}

function createLogService(): LogService {
  return {
    info: (message, context) => console.log(`[Foundation] ${message}`, context ?? ''),
    warn: (message, context) => console.warn(`[Foundation] ${message}`, context ?? ''),
    error: (message, context) => console.error(`[Foundation] ${message}`, context ?? ''),
    event: (event, data) => console.log(`[Foundation Event] ${event}`, data ?? '')
  }
}
